import { useEffect } from 'react'
import { wait } from '../../../../lib'
import { useOrderDetailContext } from '../../OrderDetailContext'
import { useQueryClient } from '@tanstack/react-query'
import { useBarcode } from '../../../../hooks/useBarcode'
import { useFragment, FragmentType } from '../../../../gql/fragment-masking'
import { UpdateClearanceItemPickingMutationVariables } from '../../../../gql/graphql'
import { useGQLMutation } from '../../../../hooks/GQLUtilityHooks'
import { graphql } from '../../../../gql'
import { useSettingsStore } from '../../../../contexts/SettingsContext'
import { toastError } from '../../../../lib/toastHelpers'
import { notifyPickingActivity } from './pickingActivityEvents'
import { BarcodeScanner } from '../../../../services/BarcodeScanner'

const UseOrderPicking_OrderFragment = graphql(`
  fragment UseOrderPicking_OrderFragment on Order {
    id
    processStatus {
      id
    }
    items {
      id
      clearanceItems {
        id
      }
    }
    clearanceItems {
      id
      amount
      picked
      packed
      product {
        id
        fullName
        ean
        ean2
        ean3
        ean4
        ean5
        code
      }
    }
  }
`)

// Define the mutation document directly in this file
const UpdateClearanceItemPickingHook = graphql(`
  mutation UpdateClearanceItemPickingHook($id: String!, $order: Int!, $picked: Int, $packed: Int) {
    updateClearanceItem(id: $id, order: $order, picked: $picked, packed: $packed) {
      data {
        id
        picked
        packed
        amount
      }
      errors
    }
  }
`)

const UpdateOrderItemsPickingHook = graphql(`
  mutation UpdateOrderItemsPickingHook($id: Int!, $picked: Boolean, $packed: Boolean) {
    updateOrderItem(id: $id, picked: $picked, packed: $packed) {
      data {
        id
        picked
        packed
      }
      errors
    }
  }
`)

export const useOrderPicking = () => {
  const context = useOrderDetailContext()
  const order = useFragment(UseOrderPicking_OrderFragment, context.order)
  const queryClient = useQueryClient()
  const settings = useSettingsStore()

  const { mutate } = useGQLMutation(UpdateClearanceItemPickingHook, ['updateClearanceItem', order?.id], {
    onSuccess: data => {
      console.log('data', data)
      queryClient.invalidateQueries({ queryKey: ['order', { id: order?.id }] })
      queryClient.setQueryData(['order', { id: order?.id }], (old: any) => {
        return {
          order: {
            ...old.order,
            data: {
              ...old.order.data,
              clearanceItems: old.order.data.clearanceItems.map(ci => {
                if (ci.id === data.id) {
                  return { ...ci, ...data }
                }
                return ci
              }),
            },
          },
        }
      })
      const phase =
        order?.processStatus?.id === 'packing' ? 'packing' : order?.processStatus?.id === 'picking' ? 'picking' : null
      if (phase && order?.id) {
        notifyPickingActivity({ orderId: Number(order.id), phase, source: 'scanner' })
      }
    },
  })

  const { mutate: updateOrderItemsPicking } = useGQLMutation(
    UpdateOrderItemsPickingHook,
    ['updateOrderItemsPicking', order?.id],
    {
      onSuccess: data => {
        console.log('data', data)
        queryClient.invalidateQueries({ queryKey: ['order', { id: order?.id }] })
        queryClient.setQueryData(['order', { id: order?.id }], (old: any) => {
          return {
            order: {
              ...old.order,
              data: {
                ...old.order.data,
                items: old.order.data.items.map(item => {
                  if (item.id === data.id) {
                    return { ...item, ...data }
                  }
                  return item
                }),
              },
            },
          }
        })
        if (order?.id) {
          const phase = order?.processStatus?.id === 'packing' ? 'packing' : 'picking'
          notifyPickingActivity({ orderId: Number(order.id), phase, source: 'scanner' })
        }
      },
      onError: error => {
        console.error('error', error)
      },
    },
  )

  useBarcode(
    async event => {
      console.log('event', event)
      if (!order || event.type !== 'BARCODE') return

      // Ignore box/package barcodes; those are handled separately (e.g. in OrderProducts)
      if (BarcodeScanner.isBoxBarcode(event.data)) return
      // Find all masked clearance items matching the scanned code/ean
      const matchingClearanceItems = order.clearanceItems.filter(ci => {
        if (!ci.product) return false
        const product = ci.product
        return (
          product?.code === event.data ||
          product?.ean === event.data ||
          product?.ean2 === event.data ||
          product?.ean3 === event.data ||
          product?.ean4 === event.data ||
          product?.ean5 === event.data
        )
      })

      console.log({ matchingClearanceItems })

      // Alert if product doesn't exist in this order
      if (matchingClearanceItems.length === 0) {
        toastError(`Produkt s kódem «${event.data}» nebyl v této objednávce nalezen.`)
        return
      }

      // Check if all matching items are already fully picked/packed
      const isPicking = order.processStatus?.id === 'picking'
      const isPacking = order.processStatus?.id === 'packing'

      const allAlreadyProcessed = matchingClearanceItems.every(ci => {
        if (isPicking) {
          return ci.picked >= ci.amount
        }
        if (isPacking) {
          return ci.packed >= ci.amount
        }
        return false
      })

      if (allAlreadyProcessed) {
        const firstProduct = matchingClearanceItems[0]?.product
        const productName = firstProduct?.fullName || event.data
        if (isPicking) {
          toastError(`Produkt «${productName}» je již kompletně vybrán.`)
        } else if (isPacking) {
          toastError(`Produkt «${productName}» je již kompletně zabalen.`)
        }
        return
      }

      // Only pick/pack one at a time (the first not fully picked/packed)
      let handled = false
      for (const maskedClearanceItem of matchingClearanceItems) {
        if (handled) break
        const clearanceItem = maskedClearanceItem
        const product = clearanceItem.product
        const updates: UpdateClearanceItemPickingMutationVariables = {
          id: clearanceItem.id!,
          order: order.id!,
        }
        if (order.processStatus?.id === 'picking') {
          // Only pick if not already fully picked
          if (clearanceItem.picked < clearanceItem.amount) {
            if (settings.pickingPieceByPiece) {
              const nextPicked = Math.min((clearanceItem.picked ?? 0) + 1, clearanceItem.amount ?? 0)
              updates.picked = nextPicked
              mutate(updates)
              // if (nextPicked === clearanceItem.amount) {
              //   order.items.forEach(item => {
              //     if (item.clearanceItems.some(ci => ci.id === clearanceItem.id)) {
              //       return updateOrderItemsPicking({ id: item.id!, picked: true })
              //     }
              //   })
              // }
              handled = true
            } else {
              const shouldConfirm = clearanceItem.amount > 1
              const confirmMsg = `Mate vybrano všech ${clearanceItem.amount} kusů produktu «${product?.fullName}»?`
              if (!shouldConfirm || window.confirm(confirmMsg)) {
                updates.picked = clearanceItem.amount
                mutate(updates)
                order.items.forEach(item => {
                  if (item.clearanceItems.some(ci => ci.id === clearanceItem.id)) {
                    return updateOrderItemsPicking({ id: item.id!, picked: true })
                  }
                })
                handled = true
              }
            }
          }
        }
        if (order.processStatus?.id === 'packing') {
          // Only pack if not already fully packed
          if (clearanceItem.packed < clearanceItem.amount) {
            if (settings.pickingPieceByPiece) {
              const nextPacked = Math.min((clearanceItem.packed ?? 0) + 1, clearanceItem.amount ?? 0)
              updates.packed = nextPacked
              mutate(updates)
              // if (nextPacked === clearanceItem.amount) {
              //   order.items.forEach(item => {
              //     if (item.clearanceItems.some(ci => ci.id === clearanceItem.id)) {
              //       return updateOrderItemsPicking({ id: item.id!, packed: true })
              //     }
              //   })
              // }
              handled = true
            } else {
              const shouldConfirm = clearanceItem.amount > 1
              const confirmMsg = `Máte zabaleno všech ${clearanceItem.amount} kusů produktu «${product?.fullName}»?`
              if (!shouldConfirm || window.confirm(confirmMsg)) {
                updates.packed = clearanceItem.amount
                mutate(updates)
                order.items.forEach(item => {
                  if (item.clearanceItems.some(ci => ci.id === clearanceItem.id)) {
                    const allClearanceItemsPacked = item.clearanceItems.every(itemClearanceItem => {
                      const currentClearanceItem = order.clearanceItems.find(ci => ci.id === itemClearanceItem.id)
                      if (!currentClearanceItem) return false

                      const packedAmount =
                        currentClearanceItem.id === clearanceItem.id
                          ? clearanceItem.amount
                          : (currentClearanceItem.packed ?? 0)

                      return packedAmount >= (currentClearanceItem.amount ?? 0)
                    })

                    if (allClearanceItemsPacked) {
                      updateOrderItemsPicking({ id: item.id!, packed: true })
                    }
                  }
                })
                handled = true
              }
            }
          }
        }
      }
      await wait(600)
    },
    { autoStart: true },
  )
}
